import { OmniScrollArea } from "@/components/artifacts/OmniScrollArea.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { confirmationToast, errorToast } from "@/components/ui/toast.tsx";
import { apiErrorReason } from "@/lib/http.ts";
import { cn } from "@/lib/utils.ts";
import { useSlackChannelPicker } from "@/shared/useSlackChannelPicker.ts";
import type { SlackPickerRefusalReason } from "@/types/slackChannels.ts";
import { SlackChannelChecklist } from "./SlackChannelChecklist.tsx";

type Props = {
  teamId: string;
  /** Runs once the join is queued, with how many channels it will add. */
  onJoined?: (queued: number) => void;
  /** Shows a "Not now" button beside Add (the modal's). */
  onCancel?: () => void;
  /** Height cap for the list, which scrolls inside it. */
  scrollClassName?: string;
};

const RECONNECT: SlackPickerRefusalReason = "reconnect";

/** "1 channel", "3 channels". */
function channelCount(count: number): string {
  return `${count} ${count === 1 ? "channel" : "channels"}`;
}

/**
 * The channel list and its "Add okthx" button, for the post-connect modal and
 * the in-chat card. Once the join is queued the button gives way to a line
 * saying so, so the same pick can't be sent twice.
 */
export const SlackChannelPickerForm = ({ teamId, onJoined, onCancel, scrollClassName }: Props) => {
  const { query, join, selectedCount, publicChannels, privateChannels, isSelected, toggle } =
    useSlackChannelPicker(teamId);

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-2 rounded-md border p-3">
        <Skeleton className="h-3.5 w-32" />
        <Skeleton className="h-3.5 w-44" />
        <Skeleton className="h-3.5 w-36" />
      </div>
    );
  }
  if (query.isError) {
    return (
      <p className="text-sm text-muted-foreground">
        {apiErrorReason(query.error) === RECONNECT
          ? "Reconnect Slack to add okthx to your channels."
          : "Couldn't load your Slack channels. Try again in a moment."}
      </p>
    );
  }
  if (publicChannels.length + privateChannels.length === 0) {
    return <p className="text-sm text-muted-foreground">You aren't in any Slack channels yet.</p>;
  }

  const submit = () =>
    join.mutate(undefined, {
      onSuccess: ({ queued }) => {
        if (queued > 0) confirmationToast(`Adding okthx to ${channelCount(queued)}`);
        onJoined?.(queued);
      },
      onError: (error) =>
        errorToast(
          apiErrorReason(error) === RECONNECT
            ? "Reconnect Slack to add okthx to your channels."
            : "Couldn't add okthx to your channels. Please try again.",
        ),
    });

  const addLabel =
    selectedCount === 0 ? "Add okthx" : `Add okthx to ${channelCount(selectedCount)}`;

  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-hidden rounded-md border">
        <OmniScrollArea className={cn("max-h-[245px]", scrollClassName)}>
          <SlackChannelChecklist
            publicChannels={publicChannels}
            privateChannels={privateChannels}
            isSelected={isSelected}
            onToggle={toggle}
            disabled={join.isPending || join.isSuccess}
          />
        </OmniScrollArea>
      </div>
      {join.isSuccess ? (
        <p className="text-sm text-muted-foreground">
          {join.data.queued > 0
            ? `okthx is joining ${channelCount(join.data.queued)}. It can take a few minutes.`
            : "okthx is already in those channels."}
        </p>
      ) : (
        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button
              variant="ghost"
              onClick={onCancel}
              disabled={join.isPending}
              data-statsig-name="slack-channel-picker-skip"
            >
              Not now
            </Button>
          )}
          <Button
            onClick={submit}
            disabled={selectedCount === 0 || join.isPending}
            data-statsig-name="slack-channel-picker-add"
          >
            {addLabel}
          </Button>
        </div>
      )}
    </div>
  );
};
